import { useState } from 'react';
import { useTvaRates } from '../hooks/useTvaRates';

const inputClass = 'px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400';

export default function TvaSettingsPage() {
  const { rates, pcgMap, saveRates, savePcgMap, resetRates } = useTvaRates();
  const [newRateInput, setNewRateInput] = useState('');

  const handleAddRate = () => {
    const val = parseFloat(newRateInput);
    if (!isNaN(val) && val >= 0 && val <= 100 && !rates.includes(val)) {
      saveRates([...rates, val]);
    }
    setNewRateInput('');
  };

  const handleRemoveRate = (rate: number) => {
    const newRates = rates.filter((r) => r !== rate);
    saveRates(newRates);
    // Remove PCG entry for this rate
    const newMap = { ...pcgMap };
    delete newMap[String(rate)];
    savePcgMap(newMap);
  };

  const handlePcgChange = (rate: number, value: string) => {
    savePcgMap({ ...pcgMap, [String(rate)]: value });
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Taux de TVA</h2>
      <p className="text-sm text-gray-500 mb-6">Configurez les taux de TVA et les comptes PCG associés.</p>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="space-y-4 mb-6">
          {rates.map((rate) => (
            <div key={rate} className="flex items-start gap-4 p-4 bg-amber-50 border border-amber-100 rounded-xl">
              <div className="flex-shrink-0 flex items-center gap-2 pt-1">
                <span className="inline-flex items-center px-3 py-1 bg-amber-100 border border-amber-200 text-amber-800 text-sm font-semibold rounded-full min-w-[56px] justify-center">
                  {rate}%
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveRate(rate)}
                  className="text-amber-400 hover:text-red-500 transition-colors font-bold text-lg leading-none"
                  title="Supprimer ce taux"
                >
                  ×
                </button>
              </div>
              <div className="flex-1">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Comptes PCG associés
                </label>
                <input
                  type="text"
                  value={pcgMap[String(rate)] || ''}
                  onChange={(e) => handlePcgChange(rate, e.target.value)}
                  placeholder="ex: 445711, 445712, 445610"
                  className={`w-full ${inputClass}`}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Saisissez les numéros de compte séparés par des virgules. Ces comptes seront automatiquement associés à ce taux lors de la saisie.
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-gray-100 pt-5">
          <p className="text-sm font-medium text-gray-700 mb-3">Ajouter un taux</p>
          <div className="flex gap-2 items-center">
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={newRateInput}
              onChange={(e) => setNewRateInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddRate()}
              placeholder="Ex: 8.5"
              className={`${inputClass} max-w-[140px]`}
            />
            <span className="text-sm text-gray-500">%</span>
            <button
              type="button"
              onClick={handleAddRate}
              disabled={!newRateInput}
              className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 disabled:opacity-40 transition-colors"
            >
              Ajouter
            </button>
            <button
              type="button"
              onClick={resetRates}
              className="px-4 py-2 bg-gray-100 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
